import { History as HistoryIcon, HistoryToggleOff as HistoryToggleOffIcon } from '@mui/icons-material';
import {
	Card,
	CircularProgress,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	Typography,
} from '@mui/material';
import { useQuery } from '@parkchirp/api-client';
import { ResourceActionsDisplay, ResourceTypesDisplay } from 'parkchirp-shared/enums';
import React, { useContext, useEffect, useState } from 'react';

import { CommonContext } from '../../contexts';
import { LIST_AUDIT_HISTORY_V2 } from '../../queries';
import { dayjs } from '../../utils';
import { AuditHistoryContextMenu } from './auditHistoryCloudWatchLink';

interface AuditHistoryTableProps {
	title?: string;
	resourceId: string;
	resourceType: string;
	timezone?: string;
}

interface AuditHistoryItem {
	id: string;
	time: string;
	resourceAction: string;
	userActionDescription: string;
	userId: string;
	xrayTraceId: string;
}

interface AuditHistoryUser {
	cognitoSub: string;
	firstName: string;
	lastName: string;
	email: string;
}

type AuditHistoryData = {
	getResourceAuditHistory: {
		history: AuditHistoryItem[];
		users: AuditHistoryUser[];
		fullHistory: boolean;
	};
};

const labelStyle: React.CSSProperties = {
	display: 'flex',
	alignItems: 'center',
	gap: 1,
	marginBottom: '10px',
};

const headerCellStyle: React.CSSProperties = {
	padding: '10px 0px 10px 10px',
};

const bodyCellStyle: React.CSSProperties = {
	padding: '5px 0px 5px 10px',
};

export const AuditHistoryTable = ({ title, resourceId, resourceType, timezone }: AuditHistoryTableProps) => {
	const [userMap, setUserMap] = useState({});
	const { AccessControlComponent } = useContext(CommonContext);

	const { loading, data } = useQuery<AuditHistoryData>(LIST_AUDIT_HISTORY_V2, {
		variables: {
			resourceId,
			resourceType,
		},
		context: {
			apiVersion: 'v2',
		},
		fetchPolicy: 'network-only',
	});

	useEffect(() => {
		if (data?.getResourceAuditHistory?.users != null) {
			const newUserMap = {};

			data.getResourceAuditHistory.users.forEach((user: AuditHistoryUser) => {
				newUserMap[user.cognitoSub] = user;
			});

			setUserMap(newUserMap);
		}
	}, [data]);

	return (
		AccessControlComponent != null && (
			<AccessControlComponent componentName='AuditHistoryTable'>
				{title != null && (
					<Typography variant='h6' gutterBottom marginTop='20px'>
						{title}
					</Typography>
				)}
				{loading ? (
					<CircularProgress color='inherit' size={20} data-cy='loading-indicator' />
				) : (
					<>
						<Typography variant='caption' sx={labelStyle}>
							{data?.getResourceAuditHistory?.fullHistory === true ? (
								<>
									<HistoryIcon />
									Full audit history available
								</>
							) : (
								<>
									<HistoryToggleOffIcon />
									{data?.getResourceAuditHistory?.history?.length > 0
										? 'Partial audit history available'
										: 'No audit history available'}
								</>
							)}
						</Typography>
						{data?.getResourceAuditHistory?.history?.length > 0 && (
							<Card>
								<TableContainer sx={{ maxHeight: 300, minWidth: 800 }}>
									<Table stickyHeader>
										<TableHead>
											<TableRow>
												<TableCell sx={headerCellStyle}>Time</TableCell>
												<TableCell sx={headerCellStyle}>Action</TableCell>
												<TableCell sx={headerCellStyle}>User</TableCell>
												<TableCell sx={headerCellStyle}>Parent Action</TableCell>
												<TableCell sx={headerCellStyle} />
											</TableRow>
										</TableHead>
										<TableBody>
											{data.getResourceAuditHistory.history.map((row: AuditHistoryItem) => {
												const user = userMap[row.userId];
												let timestamp = dayjs.utc(row.time).local().format('M/D/YYYY h:mm A z');

												if (timezone != null) {
													timestamp = dayjs
														.utc(row.time)
														.tz(timezone)
														.format('M/D/YYYY h:mm A z');
												}

												const displayType = ResourceTypesDisplay[resourceType];
												const displayAction = ResourceActionsDisplay[row.resourceAction];

												return (
													<TableRow key={row.id}>
														<TableCell sx={bodyCellStyle}>{timestamp}</TableCell>
														<TableCell sx={bodyCellStyle}>
															{displayAction} {displayType}
														</TableCell>
														<TableCell sx={bodyCellStyle}>
															{user != null ? (
																<a
																	href={`/user/?userId=${row.userId}`}
																	target='_blank'
																	rel='noreferrer'
																	style={{ color: 'blue' }}
																>
																	{user.firstName} {user.lastName} ({user.email})
																</a>
															) : (
																'Unknown User'
															)}
														</TableCell>
														<TableCell sx={bodyCellStyle}>
															{row.userActionDescription}
														</TableCell>
														<TableCell sx={bodyCellStyle}>
															<AuditHistoryContextMenu xrayTraceId={row.xrayTraceId} />
														</TableCell>
													</TableRow>
												);
											})}
										</TableBody>
									</Table>
								</TableContainer>
							</Card>
						)}
					</>
				)}
			</AccessControlComponent>
		)
	);
};
